import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { useLeadFollowUpsInfiniteQuery } from '../../../queries/Lead/useLeadFollowUpsInfiniteQuery';
import { Button } from '../../../web/components-v2/Button';
import { Skeleton } from '../../../web/components-v2/Skeleton';
import { FieldKey } from '../../viewModels/form';
import { LeadServedSource } from '../../viewModels/queue';
import { BasicPage } from '../BasicPage';
import { FollowUpCard } from './components/FollowUpCard';
import { FOLLOW_UP_COLUMNS, FollowUpRow } from './components/FollowUpRow';
import { FollowUpSection } from './components/FollowUpSection';
import { FollowUpsLegend } from './components/FollowUpsLegend';
import { FollowUpsToolbar, FollowUpsViewType } from './components/FollowUpsToolbar';
import { groupFollowUps, uniqueFollowUps } from './followUps';
import { styleSheet } from './styles';

const VIEW_TYPE_STORAGE_KEY = 'LeadView-ViewType';
const REGROUP_INTERVAL_MS = 60 * 1000;

function readViewType(): FollowUpsViewType {
	try {
		return window.localStorage.getItem(VIEW_TYPE_STORAGE_KEY) === 'card' ? 'card' : 'row';
	} catch {
		return 'row';
	}
}

function writeViewType(viewType: FollowUpsViewType) {
	try {
		window.localStorage.setItem(VIEW_TYPE_STORAGE_KEY, viewType);
	} catch {
		// The view choice is a convenience; losing it is fine.
	}
}

export const FollowUpsContainer: React.FC = () => {
	const history = useHistory();
	const [sdr, setSdr] = React.useState<Api.IUser | null>(null);
	const [outcome, setOutcome] = React.useState<FieldKey>(FieldKey.Unknown);
	const [viewType, setViewType] = React.useState<FollowUpsViewType>(readViewType);
	const [now, setNow] = React.useState(() => new Date());

	React.useEffect(() => {
		const timer = window.setInterval(() => setNow(new Date()), REGROUP_INTERVAL_MS);
		return () => window.clearInterval(timer);
	}, []);

	const followUpsQuery = useLeadFollowUpsInfiniteQuery({
		userId: sdr?.id,
		outcomeName: outcome !== FieldKey.Unknown ? outcome : undefined,
		meta: { errorLogging: { category: 'FollowUps', action: 'FollowUpsLoad-Error' } },
	});
	const {
		data,
		error,
		fetchNextPage,
		hasNextPage,
		isFetchNextPageError,
		isFetching,
		isFetchingNextPage,
		isLoading,
		isRefetchError,
		refetch,
	} = followUpsQuery;
	const items = React.useMemo(() => uniqueFollowUps(data?.pages.flatMap(page => page.values ?? []) ?? []), [data]);
	const sections = React.useMemo(() => groupFollowUps(items, now), [items, now]);

	const onViewTypeChange = (next: FollowUpsViewType) => {
		setViewType(next);
		writeViewType(next);
	};

	const onOpen = (companyId?: string) => {
		if (companyId) {
			history.push({ pathname: '/queue', search: `?from=${LeadServedSource.FollowUp}&companyId=${companyId}` });
		}
	};

	const loadMore = () => {
		if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
			fetchNextPage();
		}
	};

	const renderBody = () => {
		if (isLoading) {
			return (
				<div className={css(styleSheet.list)} aria-label='Loading follow ups'>
					{[0, 1, 2, 3].map(index => (
						<Skeleton key={index} styles={[styleSheet.skeletonRow]} />
					))}
				</div>
			);
		}
		if (error && !items.length) {
			return (
				<div className={css(styleSheet.message)} role='alert'>
					<span>We couldn't load your follow ups.</span>
					<Button
						type='button'
						variant='outline'
						size='sm'
						aria-label='Try again'
						isLoading={isFetching}
						onClick={() => refetch()}
					>
						Try again
					</Button>
				</div>
			);
		}
		if (!items.length) {
			return <div className={css(styleSheet.message)}>No follow ups</div>;
		}
		return (
			<>
				{isRefetchError ? (
					<div className={css(styleSheet.loadMoreError)} role='alert'>
						<span>We couldn't refresh your follow ups.</span>
						<Button
							type='button'
							variant='outline'
							size='sm'
							aria-label='Try again'
							isLoading={isFetching}
							onClick={() => refetch()}
						>
							Try again
						</Button>
					</div>
				) : null}
				{viewType === 'row' ? (
					<div className={css(styleSheet.columns)} aria-hidden='true'>
						{FOLLOW_UP_COLUMNS.map(column => (
							<span key={column}>{column}</span>
						))}
					</div>
				) : null}
				{sections.map(section => (
					<FollowUpSection key={section.bucket} bucket={section.bucket} label={section.label}>
						<div className={css(viewType === 'row' ? styleSheet.list : styleSheet.cardGrid)}>
							{section.items.map((lead, index) => {
								const key = lead.company?.id ?? `no-company-${index}`;
								return viewType === 'row' ? (
									<FollowUpRow key={key} lead={lead} onOpen={onOpen} />
								) : (
									<FollowUpCard key={key} lead={lead} onOpen={onOpen} />
								);
							})}
						</div>
					</FollowUpSection>
				))}
				{isFetchingNextPage ? <Skeleton styles={[styleSheet.skeletonRow]} /> : null}
				{isFetchNextPageError ? (
					<div className={css(styleSheet.loadMoreError)} role='alert'>
						<span>We couldn't load more follow ups.</span>
						<Button type='button' variant='outline' size='sm' onClick={() => fetchNextPage()}>
							Try again
						</Button>
					</div>
				) : null}
				<Waypoint bottomOffset='-200px' onEnter={loadMore} />
			</>
		);
	};

	return (
		<BasicPage
			appearance='light'
			title='Follow ups'
			titleAddon={<FollowUpsLegend />}
			headerActions={
				<FollowUpsToolbar
					sdr={sdr}
					onSdrChange={setSdr}
					outcome={outcome}
					onOutcomeChange={setOutcome}
					viewType={viewType}
					onViewTypeChange={onViewTypeChange}
				/>
			}
		>
			<div className={css(styleSheet.body)}>{renderBody()}</div>
		</BasicPage>
	);
};
